import React, { useEffect, useState } from 'react';
import { Layout, PageHeader, Row, Col, Spin, Switch, Modal, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import UserProfileList from './UserProfileList';
import UserProfileCard from './UserProfileCard';
import { EnvironmentOutlined, SolutionOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { WEBSITE_URL } from '../Helper';
import * as UserDataService from '../services/GetUserData';
import * as Actions from '../store/actions/user';

const { Content } = Layout;

const UserProfilePage = ({ profiles: propProfiles }) => {
  const dispatch = useDispatch();
  const reduxProfiles = useSelector((state) => state.User?.userDetails?.profiles || state.user?.userData?.profiles);
  const [loading, setLoading] = useState(!reduxProfiles && !propProfiles);

  // Single source of truth: directly derived from Redux or initial prop
  const profiles = (reduxProfiles && reduxProfiles.length > 0) ? reduxProfiles : (propProfiles || []);

  useEffect(() => {
    // Professional standard: fetch fresh profile data once on mount and update Redux store
    dispatch(UserDataService.getUserData())
      .then((res) => {
        if (res) {
          dispatch(Actions.set_user_auth(res));
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [dispatch]);

  const userDetails = useSelector((state) => state.User?.userDetails || state.user?.userData);
  const userRole = userDetails?.user_role;
  const isInspectorRole = userRole === 'um_inspector' || userRole === 'um_flooring-inspector';
  const isFreeInspector = Boolean(
    !userDetails?.is_admin &&
    isInspectorRole &&
    (String(userDetails?.membersdhip_level) === '1' || userDetails?.limited_access === true)
  );
  const isPaidInspector = isInspectorRole && !isFreeInspector;

  const hasStandaloneNonInspectorProfile = Array.isArray(profiles) && profiles.some((p) =>
    (p.post_type === 'consultant_profile' && !p.is_inspector_addon) ||
    p.post_type === 'cleaning_restoration' ||
    p.post_type === 'trusted_dealer' ||
    p.post_type === 'installer_profile'
  );
  const showAddLocation = isInspectorRole && !hasStandaloneNonInspectorProfile;

  const inspectorProfiles = Array.isArray(profiles) ? profiles.filter(p => p.post_type === 'inspector_profile') : [];
  const consultantProfiles = Array.isArray(profiles) ? profiles.filter(p => p.post_type === 'consultant_profile') : [];
  const hasConsultantProfile = consultantProfiles.length > 0;
  const isConsultantExpired = hasConsultantProfile && consultantProfiles[0]?.status?.toLowerCase() === 'expired';
  const isConsultantActive = hasConsultantProfile && !isConsultantExpired;
  const otherProfiles = Array.isArray(profiles) ? profiles.filter(p => p.post_type !== 'inspector_profile' && p.post_type !== 'consultant_profile') : [];

  const [isAddonModalVisible, setIsAddonModalVisible] = useState(false);
  const [isActiveInfoModalVisible, setIsActiveInfoModalVisible] = useState(false);

  const handleOpenAddonModal = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (isConsultantExpired) {
      const insProfileId = consultantProfiles[0]?.ID || consultantProfiles[0]?.Id || '';
      window.location.href = `${WEBSITE_URL}/product/consultant-addon/?renewal_profile_id=${insProfileId}`;
    } else if (hasConsultantProfile) {
      setIsActiveInfoModalVisible(true);
    } else {
      setIsAddonModalVisible(true);
    }
  };

  return (
    <Layout style={{ background: '#f8fafc', minHeight: '80vh' }}>
      <PageHeader title="User Profiles" />

      {/* Top Action & Switcher Bar */}
      {(showAddLocation || isPaidInspector) && (
        <div className="top-action-bar-wrapper" style={{ padding: '0 24px', marginBottom: 24 }}>
          <div className="top-action-bar-inner" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
            background: '#ffffff',
            padding: '12px 18px',
            borderRadius: 14,
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}>
            {/* Left: Add Inspector Location Card */}
            {showAddLocation ? (
              <div className='Purchasing-location-button' style={{ width: 'auto' }}>
                <a
                  id="Purchasing-Other-Location"
                  className="inspector-location-action-card"
                  href={`${WEBSITE_URL}/product/new-location/`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 16,
                    background: '#f0f9ff',
                    padding: '10px 18px',
                    borderRadius: 12,
                    border: '1.5px solid #bae6fd',
                    boxShadow: '0 2px 8px rgba(2, 132, 199, 0.08)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.25s ease',
                    textDecoration: 'none',
                  }}
                >
                  <div className="inspector-location-card-left" style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                    <div
                      className="inspector-location-icon-box"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: '#e0f2fe',
                        color: '#0284c7',
                        border: '1px solid #bae6fd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      <EnvironmentOutlined />
                    </div>

                    <div className="inspector-location-texts" style={{ display: 'flex', flexDirection: 'column', minWidth: 0, marginRight: 6 }}>
                      <span className="inspector-location-title" style={{ fontSize: 14, fontWeight: 700, color: '#0a497b' }}>
                        Inspector Profile Location
                      </span>
                      <span className="inspector-location-subtext" style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                        Purchase additional location for Inspector
                      </span>
                    </div>
                  </div>

                  {/* Fully Rounded Pill Button */}
                  <div
                    className="inspector-location-pill-btn"
                    style={{
                      height: 38,
                      padding: '0 20px',
                      borderRadius: 50,
                      background: '#004792',
                      color: '#ffffff',
                      fontSize: 13,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      boxShadow: '0 3px 10px rgba(0, 71, 146, 0.28)',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <PlusOutlined style={{ fontSize: 13 }} />
                    <span>Add Location</span>
                  </div>
                </a>
              </div>
            ) : <div />}

            {/* Right: Enable Flooring Consultant Profile Switcher Card */}
            {isPaidInspector && (
              <div 
                className="consultant-addon-switcher-card"
                onClick={handleOpenAddonModal}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 14,
                  background: isConsultantActive ? '#f5f3ff' : isConsultantExpired ? '#fff7ed' : '#ffffff',
                  padding: '10px 18px',
                  borderRadius: 12,
                  border: isConsultantActive ? '1.5px solid #a78bfa' : isConsultantExpired ? '1.5px solid #fdba74' : '1.5px solid #cbd5e1',
                  boxShadow: isConsultantActive ? '0 2px 10px rgba(124, 58, 237, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.25s ease'
                }}
              >
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: isConsultantActive 
                    ? 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)' 
                    : isConsultantExpired 
                    ? '#ffedd5' 
                    : '#ede9fe',
                  color: isConsultantActive ? '#ffffff' : isConsultantExpired ? '#ea580c' : '#6d28d9',
                  border: isConsultantActive ? 'none' : isConsultantExpired ? '1px solid #fed7aa' : '1px solid #ddd6fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  boxShadow: isConsultantActive ? '0 2px 6px rgba(124, 58, 237, 0.3)' : 'none',
                  transition: 'all 0.25s ease'
                }}>
                  <SolutionOutlined />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                      Enable Flooring Consultant Profile
                    </span>
                    <Tag 
                      color={isConsultantActive ? 'purple' : isConsultantExpired ? 'orange' : 'default'} 
                      style={{ margin: 0, fontSize: 11, borderRadius: 10, fontWeight: 700, padding: '0 8px' }}
                    >
                      {isConsultantActive ? 'ACTIVE' : isConsultantExpired ? 'EXPIRED' : 'OFF'}
                    </Tag>
                  </div>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {isConsultantActive 
                      ? 'Profile active · Listed on directory & interactive map' 
                      : isConsultantExpired 
                      ? 'Subscription expired · Click to renew add-on' 
                      : 'Click to enable consultant profile add-on'}
                  </span>
                </div>

                <div style={{ marginLeft: 6, pointerEvents: 'none' }}>
                  <Switch 
                    checked={isConsultantActive}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    style={{
                      backgroundColor: isConsultantActive ? '#7c3aed' : undefined
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Controlled Add-on Activation Modal (Guaranteed single instance, 1-click cancel) */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 16, fontWeight: 700, color: '#1e1b4b' }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)'
            }}>
              <SolutionOutlined />
            </div>
            <span>Enable Flooring Consultant Profile</span>
          </div>
        }
        visible={isAddonModalVisible}
        onCancel={() => setIsAddonModalVisible(false)}
        footer={[
          <button
            key="cancel"
            type="button"
            onClick={() => setIsAddonModalVisible(false)}
            style={{
              padding: '8px 18px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#475569',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              marginRight: 10
            }}
          >
            Cancel
          </button>,
          <button
            key="submit"
            type="button"
            onClick={() => {
              setIsAddonModalVisible(false);
              const insUserId = userDetails?.user_id || userDetails?.id || userDetails?.ID || '';
              const insEmail = encodeURIComponent(userDetails?.user_email || '');
              window.location.href = `${WEBSITE_URL}/product/consultant-addon/?inspector_user_id=${insUserId}&inspector_email=${insEmail}`;
            }}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(124, 58, 237, 0.3)'
            }}
          >
            Proceed to Setup & Activation →
          </button>
        ]}
        width={560}
        destroyOnClose
        centered
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{
            color: '#1e293b',
            fontSize: 15,
            lineHeight: 1.6,
            fontWeight: 600,
            margin: '0 0 16px',
            background: '#f8fafc',
            borderLeft: '4px solid #7c3aed',
            padding: '12px 14px',
            borderRadius: '0 8px 8px 0'
          }}>
            Create a professional profile on Floor Detective and make it easier for flooring professionals and consumers to find your consulting services.
          </p>

          <div style={{
            background: '#faf5ff',
            border: '1px solid #e9d5ff',
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 16
          }}>
            <div style={{ fontSize: 13, color: '#581c87', fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>✨ Key Consultant Profile Benefits:</span>
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
              <li>Get published on the nationwide <strong>Flooring Consultants</strong> directory</li>
              <li>Geocoded & plotted on the interactive consultant locator map</li>
              <li>Showcase expert witness, forensic consulting & moisture testing expertise</li>
              <li>Dedicated consultant profile management alongside your inspector locations</li>
              <li>Instant automated profile activation upon checkout</li>
            </ul>
          </div>

          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
            Would you like to proceed to setup and activate your consultant profile?
          </p>
        </div>
      </Modal>

      {/* Controlled Active Info Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 16, fontWeight: 700, color: '#0a497b' }}>
            <InfoCircleOutlined style={{ color: '#0a497b', fontSize: 20 }} />
            <span>Flooring Consultant Profile Active</span>
          </div>
        }
        visible={isActiveInfoModalVisible}
        onCancel={() => setIsActiveInfoModalVisible(false)}
        footer={[
          <button
            key="close"
            type="button"
            onClick={() => setIsActiveInfoModalVisible(false)}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#0a497b',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        ]}
        width={480}
        destroyOnClose
        centered
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
            Your <strong>Flooring Consultant Profile</strong> add-on is active and published on the Floor Detective directory and map.
          </p>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
            To edit your consultant profile details, photo, or specialties, click the <strong>Edit</strong> button on your Flooring Consultant card below.
          </p>
        </div>
      </Modal>

      <Content style={{ padding: '0 24px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" tip="Loading User Profiles..." />
          </div>
        ) : isInspectorRole ? (
          <div>
            {/* Inspector Profile Section Heading */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0a497b', margin: 0 }}>
                Inspector Profile
              </h3>
              {inspectorProfiles.length > 0 && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 12px',
                    borderRadius: 20,
                    fontSize: 12.5,
                    fontWeight: 600,
                    background: '#e0f2fe',
                    color: '#0284c7',
                    border: '1px solid #bae6fd',
                    boxShadow: '0 1px 2px rgba(2, 132, 199, 0.08)'
                  }}
                >
                  <EnvironmentOutlined style={{ fontSize: 13 }} />
                  <span>
                    {inspectorProfiles.length} {inspectorProfiles.length === 1 ? 'Location' : 'Locations'}
                  </span>
                </span>
              )}
            </div>

            {/* Inspector Profiles */}
            <UserProfileList profiles={[...inspectorProfiles, ...otherProfiles]} />

            {/* Consultant Profile Section: Only shown when Consultant Profile is enabled */}
            {isPaidInspector && hasConsultantProfile && (
              <div
                style={{
                  marginTop: 24,
                  paddingTop: 24,
                  borderTop: '1.5px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0a497b', margin: 0 }}>
                    Flooring Consultant Profile
                  </h3>
                  <Tag color={isConsultantActive ? "#7c3aed" : "#ea580c"} style={{ borderRadius: 10, padding: '1px 10px', fontWeight: 600 }}>
                    {isConsultantActive ? "Active Add-on" : "Expired Add-on"}
                  </Tag>
                </div>

                <Row gutter={16}>
                  {consultantProfiles.map((profile) => (
                    <Col key={profile.ID || profile.Id} xs={24} sm={24} md={24} lg={12} xl={8} className="mb-3">
                      <UserProfileCard profile={profile} />
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </div>
        ) : (
          <UserProfileList profiles={profiles} />
        )}
      </Content>
    </Layout>
  );
};

export default UserProfilePage;
